/**
 * lut-builder.ts — Step 4: 3D LUT construction
 *
 * Builds a pre-press correction LUT from the 4 structural measurement axes:
 *   Primary  : R (C ink), G (M ink), B (Y ink)
 *   Diagonal : N (neutral grey, R≈G≈B)
 *
 * For each grid node:
 *   n = min(C,M,Y)           — neutral component, corrected by N-axis spline
 *   C−n, M−n, Y−n            — per-channel non-neutral ink, corrected by C/M/Y
 *                               primary splines independently
 *
 * Each channel is corrected by its own measured primary response. Binary
 * overprint measurements (RG, RB, GB groups) are used only in diagnostics
 * and domain suggestion, not in the correction function itself.
 */

import type {
  DeviceAnchors,
  LUT3D,
  LUTOptions,
  LUTValidation,
  MeasuredPatch,
  WorkingDomain,
} from '@/types';

// =============================================================================
// Monotone cubic spline (Fritsch-Carlson)
// =============================================================================

interface Spline {
  xs: number[];
  ys: number[];
  ms: number[];
}

function buildSpline(xs: number[], ys: number[]): Spline {
  const n = xs.length;
  const ms = new Array<number>(n).fill(0);
  if (n === 1) return { xs, ys, ms };

  const delta: number[] = [];
  for (let i = 0; i < n - 1; i++) {
    const dx = xs[i + 1]! - xs[i]!;
    delta.push(dx === 0 ? 0 : (ys[i + 1]! - ys[i]!) / dx);
  }
  ms[0]     = delta[0]!;
  ms[n - 1] = delta[n - 2]!;
  for (let i = 1; i < n - 1; i++) ms[i] = (delta[i - 1]! + delta[i]!) / 2;

  for (let i = 0; i < n - 1; i++) {
    if (Math.abs(delta[i]!) < 1e-12) { ms[i] = ms[i + 1] = 0; continue; }
    const alpha = ms[i]!     / delta[i]!;
    const beta  = ms[i + 1]! / delta[i]!;
    const h2    = alpha * alpha + beta * beta;
    if (h2 > 9) { const sc = 3 / Math.sqrt(h2); ms[i] = (ms[i] ?? 0) * sc; ms[i + 1] = (ms[i + 1] ?? 0) * sc; }
  }
  return { xs, ys, ms };
}

function evalSpline(sp: Spline, x: number): number {
  const { xs, ys, ms } = sp;
  const n = xs.length;
  if (n === 1 || x <= xs[0]!)  return ys[0]!;
  if (x >= xs[n - 1]!)         return ys[n - 1]!;
  let lo = 0, hi = n - 2;
  while (lo < hi) { const mid = (lo + hi) >> 1; if (xs[mid + 1]! < x) lo = mid + 1; else hi = mid; }
  const h   = xs[lo + 1]! - xs[lo]!;
  const t   = h === 0 ? 0 : (x - xs[lo]!) / h;
  const t2  = t * t, t3 = t2 * t;
  return (2*t3 - 3*t2 + 1) * ys[lo]! + (t3 - 2*t2 + t) * h * ms[lo]! +
         (-2*t3 + 3*t2) * ys[lo + 1]! + (t3 - t2) * h * ms[lo + 1]!;
}

function invertSpline(sp: Spline, y: number): number {
  const { xs, ys } = sp;
  const n = xs.length;
  if (y <= ys[0]!)    return xs[0]!;
  if (y >= ys[n-1]!)  return xs[n-1]!;
  let lo = xs[0]!, hi = xs[n-1]!;
  for (let iter = 0; iter < 60; iter++) {
    const mid = (lo + hi) / 2;
    const f   = evalSpline(sp, mid);
    if (Math.abs(f - y) < 1e-4) return mid;
    if (f < y) lo = mid; else hi = mid;
  }
  return (lo + hi) / 2;
}

// =============================================================================
// Axis spline extraction  (rgb_channel_val → L*, monotone-increasing)
// =============================================================================

/**
 * Build a spline mapping a single RGB channel value (0–255) to measured L*.
 * More channel value = less ink = lighter = higher L*, so spline is increasing.
 * The paper-white anchor (channel=255, L*=Lwhite) is appended if not already
 * present in the filtered in-domain patches.
 */
function buildAxisSpline(
  patches: MeasuredPatch[],
  group:   MeasuredPatch['group'],
  ch:      0 | 1 | 2,
  Lwhite:  number,
): Spline | null {
  const raw: [number, number][] = patches
    .filter(p => p.group === group && !p.flags.outOfDomain)
    .map(p  => [p.rgb[ch], p.lab[0]] as [number, number]);

  raw.sort((a, b) => a[0] - b[0]);

  const uniq: [number, number][] = [];
  for (const pt of raw) {
    if (uniq.length === 0 || pt[0] !== uniq[uniq.length - 1]![0]) uniq.push(pt);
  }

  // Anchor paper-white at rgb=255 when not already present
  if (uniq.length === 0 || uniq[uniq.length - 1]![0] < 250) {
    uniq.push([255, Lwhite]);
  }

  if (uniq.length < 2) return null;
  return buildSpline(uniq.map(p => p[0]), uniq.map(p => p[1]));
}

// =============================================================================
// Per-axis linearisation correction function
// =============================================================================

/**
 * Returns corrFn(u_ideal) → u_actual, where u is ink level in [0, domainLimit].
 *
 * The ideal response on this axis is linear in L*:
 *   L*(u) = Lwhite + (u / domainLimit) * (LmaxAxis − Lwhite)
 *
 * corrFn finds the actual ink level that the printer needs to send to achieve
 * that target L*, by inverting the measured axis spline.
 *
 * Returns identity when data are insufficient or axis has no L* range.
 */
function buildCorrFn(
  sp:          Spline | null,
  domainLimit: number,
  Lwhite:      number,
  LmaxAxis:    number,
): (u: number) => number {
  if (!sp || domainLimit < 1e-6 || Math.abs(LmaxAxis - Lwhite) < 0.1) {
    return (u) => u;
  }
  return (u: number) => {
    const t       = Math.max(0, Math.min(1, u / domainLimit));
    const targetL = Lwhite + t * (LmaxAxis - Lwhite);
    const rgbVal  = invertSpline(sp, targetL);   // 0–255
    return 1 - rgbVal / 255;                      // → ink level 0–1
  };
}

// =============================================================================
// 3D grid helpers
// =============================================================================

/** Flat index into the Float32Array for node (ri, gi, bi). B varies fastest. */
function idx(ri: number, gi: number, bi: number, gs: number): number {
  return (ri * gs * gs + gi * gs + bi) * 3;
}

/** Grid input value for index i in a grid of size gs (0–255). */
function gridVal(i: number, gs: number): number {
  return gs === 1 ? 0 : i * 255 / (gs - 1);
}

// =============================================================================
// LUT application — trilinear interpolation
// =============================================================================

function trilinear(
  data: Float32Array, gs: number, ch: number,
  ri0: number, ri1: number, gi0: number, gi1: number, bi0: number, bi1: number,
  fr: number, fg: number, fb: number,
): number {
  const c000 = data[idx(ri0, gi0, bi0, gs) + ch]!;
  const c001 = data[idx(ri0, gi0, bi1, gs) + ch]!;
  const c010 = data[idx(ri0, gi1, bi0, gs) + ch]!;
  const c011 = data[idx(ri0, gi1, bi1, gs) + ch]!;
  const c100 = data[idx(ri1, gi0, bi0, gs) + ch]!;
  const c101 = data[idx(ri1, gi0, bi1, gs) + ch]!;
  const c110 = data[idx(ri1, gi1, bi0, gs) + ch]!;
  const c111 = data[idx(ri1, gi1, bi1, gs) + ch]!;
  const c00 = c000 + fb * (c001 - c000);
  const c01 = c010 + fb * (c011 - c010);
  const c10 = c100 + fb * (c101 - c100);
  const c11 = c110 + fb * (c111 - c110);
  const c0  = c00  + fg * (c01  - c00);
  const c1  = c10  + fg * (c11  - c10);
  return c0 + fr * (c1 - c0);
}

// =============================================================================
// LUT application — tetrahedral interpolation (Sakamoto)
// =============================================================================

function tetrahedral(
  data: Float32Array, gs: number, ch: number,
  ri0: number, ri1: number, gi0: number, gi1: number, bi0: number, bi1: number,
  fr: number, fg: number, fb: number,
): number {
  const c000 = data[idx(ri0, gi0, bi0, gs) + ch]!;
  const c001 = data[idx(ri0, gi0, bi1, gs) + ch]!;
  const c010 = data[idx(ri0, gi1, bi0, gs) + ch]!;
  const c011 = data[idx(ri0, gi1, bi1, gs) + ch]!;
  const c100 = data[idx(ri1, gi0, bi0, gs) + ch]!;
  const c101 = data[idx(ri1, gi0, bi1, gs) + ch]!;
  const c110 = data[idx(ri1, gi1, bi0, gs) + ch]!;
  const c111 = data[idx(ri1, gi1, bi1, gs) + ch]!;
  if (fr >= fg) {
    if (fg >= fb) return (1-fr)*c000 + (fr-fg)*c100 + (fg-fb)*c110 + fb*c111;
    if (fr >= fb) return (1-fr)*c000 + (fr-fb)*c100 + (fb-fg)*c101 + fg*c111;
    return           (1-fb)*c000 + (fb-fr)*c001 + (fr-fg)*c101 + fg*c111;
  } else {
    if (fb >= fg) return (1-fb)*c000 + (fb-fg)*c001 + (fg-fr)*c011 + fr*c111;
    if (fb >= fr) return (1-fg)*c000 + (fg-fb)*c010 + (fb-fr)*c011 + fr*c111;
    return           (1-fg)*c000 + (fg-fr)*c010 + (fr-fb)*c110 + fb*c111;
  }
}

// =============================================================================
// Public: applyLUT
// =============================================================================

export function applyLUT(
  lut: LUT3D,
  r: number, g: number, b: number,
): [number, number, number] {
  const { data, gridSize: gs, options } = lut;
  const posR = Math.max(0, Math.min(gs - 1, r * (gs - 1) / 255));
  const posG = Math.max(0, Math.min(gs - 1, g * (gs - 1) / 255));
  const posB = Math.max(0, Math.min(gs - 1, b * (gs - 1) / 255));
  const ri0 = Math.min(Math.floor(posR), gs - 2);
  const gi0 = Math.min(Math.floor(posG), gs - 2);
  const bi0 = Math.min(Math.floor(posB), gs - 2);
  const ri1 = ri0 + 1, gi1 = gi0 + 1, bi1 = bi0 + 1;
  const fr = posR - ri0, fg = posG - gi0, fb = posB - bi0;
  const interp = options.interpolation === 'tetrahedral' ? tetrahedral : trilinear;
  return [
    Math.max(0, Math.min(255, interp(data, gs, 0, ri0, ri1, gi0, gi1, bi0, bi1, fr, fg, fb))),
    Math.max(0, Math.min(255, interp(data, gs, 1, ri0, ri1, gi0, gi1, bi0, bi1, fr, fg, fb))),
    Math.max(0, Math.min(255, interp(data, gs, 2, ri0, ri1, gi0, gi1, bi0, bi1, fr, fg, fb))),
  ];
}

// =============================================================================
// Public: validateLUT
// =============================================================================

export function validateLUT(lut: LUT3D): LUTValidation {
  const { data, gridSize: gs } = lut;
  let violations = 0;
  for (let gi = 0; gi < gs; gi++)
    for (let bi = 0; bi < gs; bi++)
      for (let ri = 1; ri < gs; ri++)
        if (data[idx(ri, gi, bi, gs)]! < data[idx(ri-1, gi, bi, gs)]! - 1e-4) violations++;
  for (let ri = 0; ri < gs; ri++)
    for (let bi = 0; bi < gs; bi++)
      for (let gi = 1; gi < gs; gi++)
        if (data[idx(ri, gi, bi, gs) + 1]! < data[idx(ri, gi-1, bi, gs) + 1]! - 1e-4) violations++;
  for (let ri = 0; ri < gs; ri++)
    for (let gi = 0; gi < gs; gi++)
      for (let bi = 1; bi < gs; bi++)
        if (data[idx(ri, gi, bi, gs) + 2]! < data[idx(ri, gi, bi-1, gs) + 2]! - 1e-4) violations++;
  const wb = idx(gs-1, gs-1, gs-1, gs);
  const wErr = Math.abs(data[wb]! - 255) + Math.abs(data[wb+1]! - 255) + Math.abs(data[wb+2]! - 255);
  if (wErr > 0.5) console.warn('LUT white anchor violated:', wErr);
  return { monotonic: violations === 0, nonMonotonicCount: violations, maxError: 0, meanError: 0, neutralDeltaEMax: 0, neutralDeltaEMean: 0 };
}

// =============================================================================
// Public: buildLUT
// =============================================================================

/**
 * Build a 3D correction LUT from measured patches and a working domain.
 *
 * Steps:
 *   1. Extract paper-white L* from anchors (or N-group).
 *   2. Build monotone splines for all 7 structural measurement axes.
 *   3. Determine each axis's maximum measured darkness (L* at full ink).
 *   4. Build a linearisation correction function for each axis.
 *   5. For every grid node, decompose the CMY ink vector into 7 additive
 *      structural components and apply the per-axis corrections to get the
 *      corrected ink per channel.
 *   6. Blend toward identity by options.smoothing.
 *   7. Anchor white (255,255,255) and black (0,0,0) corners exactly.
 *   8. Enforce monotonicity and return.
 */
export function buildLUT(
  patches:     MeasuredPatch[],
  domain:      WorkingDomain,
  options:     LUTOptions,
  anchors?:    DeviceAnchors,
  onProgress?: (percent: number, eta: number) => void,
): LUT3D {
  const t0 = Date.now();
  const { gridSize: gs, smoothing, maxMonotonicityIterations } = options;

  // ── 1. Paper-white and paper-black L* ──────────────────────────────────────
  const Lwhite = anchors?.white.L ?? 95;
  const Lblack = anchors?.black.L ?? 5;

  // ── 2. Build axis splines (rgb_channel_val → L*, monotone-increasing) ──────
  //
  //  N group: R=G=B vary   → use channel 0  (neutral diagonal)
  //  R group: G=B=255, R↓  → channel 0    (C-ink primary)
  //  G group: R=B=255, G↓  → channel 1    (M-ink primary)
  //  B group: R=G=255, B↓  → channel 2    (Y-ink primary)
  const nSpline = buildAxisSpline(patches, 'N', 0, Lwhite);
  const cSpline = buildAxisSpline(patches, 'R', 0, Lwhite);
  const mSpline = buildAxisSpline(patches, 'G', 1, Lwhite);
  const ySpline = buildAxisSpline(patches, 'B', 2, Lwhite);

  // ── 3. Axis minimum L* (L* at maximum measured ink = spline first point) ───
  const minL = (sp: Spline | null, fallback: number) => sp ? sp.ys[0]! : fallback;

  const LmaxN = minL(nSpline, Lblack);
  const LmaxC = minL(cSpline, Lwhite);
  const LmaxM = minL(mSpline, Lwhite);
  const LmaxY = minL(ySpline, Lwhite);

  // ── 4. Per-axis correction functions ───────────────────────────────────────
  // corrX(u_ideal) → actual_ink to send  (both in 0–1 ink units)
  const corrN = buildCorrFn(nSpline, domain.cmyMax, Lwhite, LmaxN);
  const corrC = buildCorrFn(cSpline, domain.cMax,   Lwhite, LmaxC);
  const corrM = buildCorrFn(mSpline, domain.mMax,   Lwhite, LmaxM);
  const corrY = buildCorrFn(ySpline, domain.yMax,   Lwhite, LmaxY);

  // ── 5. Populate grid ────────────────────────────────────────────────────────
  const data = new Float32Array(gs * gs * gs * 3);
  const s    = Math.max(0, Math.min(1, smoothing));

  for (let ri = 0; ri < gs; ri++) {
    for (let gi = 0; gi < gs; gi++) {
      for (let bi = 0; bi < gs; bi++) {
        const rIn = gridVal(ri, gs);
        const gIn = gridVal(gi, gs);
        const bIn = gridVal(bi, gs);

        // CMY ink levels of this input node (0 = no ink = paper white)
        const c = 1 - rIn / 255;
        const m = 1 - gIn / 255;
        const y = 1 - bIn / 255;

        // ── Per-channel correction ─────────────────────────────────────────
        //   n     = min(C,M,Y)  — neutral component → corrected by N-axis
        //   C−n, M−n, Y−n       — per-channel non-neutral ink → each corrected
        //                          by its own primary spline (C, M, Y independently)
        //
        //   Using shared binary-overprint corrections (CM/CY/MY) for both
        //   channels would assume equal C and M responses, which is invalid
        //   when primaries have different measured L* ranges.
        const n     = Math.min(c, m, y);
        const cCorr = corrC(c - n) + corrN(n);
        const mCorr = corrM(m - n) + corrN(n);
        const yCorr = corrY(y - n) + corrN(n);

        // Back to RGB 0–255, clamped
        const rOut = (1 - Math.max(0, Math.min(1, cCorr))) * 255;
        const gOut = (1 - Math.max(0, Math.min(1, mCorr))) * 255;
        const bOut = (1 - Math.max(0, Math.min(1, yCorr))) * 255;

        const base = idx(ri, gi, bi, gs);
        // s=1 → full correction; s=0 → identity (no correction)
        data[base]     = rIn * (1 - s) + rOut * s;
        data[base + 1] = gIn * (1 - s) + gOut * s;
        data[base + 2] = bIn * (1 - s) + bOut * s;
      }
    }
    if (onProgress) onProgress(Math.round(ri / (gs - 1) * 80), 0);
  }

  // ── 6. Hard-anchor cube corners ────────────────────────────────────────────
  data[0] = 0; data[1] = 0; data[2] = 0;
  const wb = idx(gs - 1, gs - 1, gs - 1, gs);
  data[wb] = 255; data[wb + 1] = 255; data[wb + 2] = 255;

  // ── 7. Monotonicity enforcement ────────────────────────────────────────────
  for (let pass = 0; pass < maxMonotonicityIterations; pass++) {
    for (let ri = 1; ri < gs; ri++)
      for (let gi = 0; gi < gs; gi++)
        for (let bi = 0; bi < gs; bi++) {
          const cur = idx(ri, gi, bi, gs), prv = idx(ri-1, gi, bi, gs);
          if (data[cur]! < data[prv]!) data[cur] = data[prv]!;
        }
    for (let ri = 0; ri < gs; ri++)
      for (let gi = 1; gi < gs; gi++)
        for (let bi = 0; bi < gs; bi++) {
          const cur = idx(ri, gi, bi, gs) + 1, prv = idx(ri, gi-1, bi, gs) + 1;
          if (data[cur]! < data[prv]!) data[cur] = data[prv]!;
        }
    for (let ri = 0; ri < gs; ri++)
      for (let gi = 0; gi < gs; gi++)
        for (let bi = 1; bi < gs; bi++) {
          const cur = idx(ri, gi, bi, gs) + 2, prv = idx(ri, gi, bi-1, gs) + 2;
          if (data[cur]! < data[prv]!) data[cur] = data[prv]!;
        }
    for (let i = 0; i < data.length; i++) {
      if (data[i]! < 0)   data[i] = 0;
      if (data[i]! > 255) data[i] = 255;
    }
  }
  if (onProgress) onProgress(100, 0);

  // ── 8. Validate and return ─────────────────────────────────────────────────
  const buildTimeMs = Date.now() - t0;
  const lut: LUT3D = { gridSize: gs, data, domain, options, buildTimeMs, validation: null };
  lut.validation = validateLUT(lut);
  return lut;
}
